<template>
  <v-dialog :value="true" max-width="1000" scrollable @input="$emit('close')">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left>mdi-chart-line</v-icon>
        {{ $t('meter-device.connected-vehicle.curve.title') }}
        <v-chip small label outlined class="ml-3">{{ charge.provider }}</v-chip>
        <v-spacer />
        <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <div class="caption grey--text mb-2">
          {{ new Date(charge.startedAt).toLocaleString() }}
          <span v-if="charge.stoppedAt">→ {{ new Date(charge.stoppedAt).toLocaleString() }}</span>
        </div>

        <!-- Full detail for this charge: retained energy, the four estimators (retained highlighted), the
             real charger (borne) reference, and the raw start/stop readings behind the reconstruction. -->
        <div class="d-flex flex-wrap mb-4" style="gap: 32px">
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.retained-energy') }}</div>
            <div class="text-h6">{{ fmtKwh(charge.energy.retainedKwh) }}</div>
            <div v-if="retainedBreakdown" class="caption grey--text">
              {{ retainedBreakdown }}
            </div>
          </div>
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.estimators') }}</div>
            <div v-for="e in allEstimators" :key="e.key" class="caption" :class="e.retained ? 'font-weight-bold' : 'grey--text'">
              <v-icon v-if="e.retained" x-small color="primary">mdi-check</v-icon>
              {{ e.label }}: {{ fmtKwh(e.value) }}
            </div>
          </div>
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.real-charger') }}</div>
            <div class="text-h6">{{ fmtKwh(charge.energy.realChargerKwh) }}</div>
          </div>
          <div>
            <div class="overline">{{ $t('meter-device.connected-vehicle.curve.raw-readings') }}</div>
            <div class="caption grey--text">
              SoC: {{ fmtPct(charge.readings.startSoc) }} → {{ fmtPct(charge.readings.stopSoc) }}<br>
              <span v-if="charge.readings.startEnergyRemainingKwh != null">
                {{ $t('meter-device.connected-vehicle.curve.energy-remaining') }}:
                {{ fmtKwh(charge.readings.startEnergyRemainingKwh) }} → {{ fmtKwh(charge.readings.stopEnergyRemainingKwh) }}<br>
              </span>
              <span v-if="charge.readings.startOdometerKm != null">
                Odo: {{ charge.readings.startOdometerKm }} → {{ charge.readings.stopOdometerKm }} km
              </span>
            </div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <v-alert v-if="!metrics.length" type="info" text dense>
            {{ $t('meter-device.connected-vehicle.curve.no-data') }}
          </v-alert>

          <template v-else>
            <!-- One curve per metric, only those with data; toggle to switch. -->
            <v-btn-toggle v-model="selectedKey" mandatory dense class="mb-3 flex-wrap">
              <v-btn v-for="m in metrics" :key="m.key" :value="m.key" small>{{ m.label }}</v-btn>
            </v-btn-toggle>

            <highcharts v-if="current" :options="chartOptions" />

            <!-- kWh ESTIMATED from this very curve (not the real charger energy — the curve's own estimate). -->
            <div v-if="current && current.estimateKwh != null" class="caption grey--text mt-2">
              {{ $t('meter-device.connected-vehicle.curve.estimated-from-curve') }}:
              <strong>{{ fmtKwh(current.estimateKwh) }}</strong>
            </div>
          </template>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import MeterDeviceRepository from "@repository/MeterDeviceRepository";

export default {
  name: 'ConnectedVehicleChargeCurveDialog',

  props: {
    charge: { type: Object, required: true },
  },

  data() {
    return {
      loading: false,
      points: [],
      offlinePeriods: [],
      selectedKey: null,
    }
  },

  computed: {
    // The candidate metrics, each kept only when the curve actually has data points. `estimateKwh` is the
    // energy derived FROM this curve (the matching estimator on the charge), never the real charger energy.
    metrics() {
      const e = this.charge.energy || {};
      const defs = [
        { key: 'soc', label: this.$t('meter-device.connected-vehicle.curve.soc'), field: 'batteryLevelPercent', unit: '%', color: '#2563EB', estimateKwh: e.socDeltaKwh },
        { key: 'energyRemaining', label: this.$t('meter-device.connected-vehicle.curve.energy-remaining'), field: 'batteryEnergyKwh', unit: 'kWh', color: '#059669', estimateKwh: e.energyRemainingDeltaKwh },
        { key: 'energyAdded', label: this.$t('meter-device.connected-vehicle.curve.energy-added'), field: 'chargeEnergyAddedKwh', unit: 'kWh', color: '#7C3AED', estimateKwh: e.energyAddedKwh },
        { key: 'power', label: this.$t('meter-device.connected-vehicle.curve.power'), field: 'chargeWattage', unit: 'kW', color: '#D97706', estimateKwh: e.powerDurationKwh, transform: w => Math.round(w / 100) / 10 },
      ];

      return defs
        .map(d => ({ ...d, data: this.seriesFor(d.field, d.transform) }))
        .filter(d => d.data.length > 0);
    },

    current() {
      return this.metrics.find(m => m.key === this.selectedKey) || this.metrics[0] || null;
    },

    // The four estimators in trust order, each labelled and flagged if it is the retained one — the full
    // detail the table now hides behind this pop-up.
    // Retained energy breakdown: duration-aware EV-Database model for SoC, else base × factor.
    retainedBreakdown() {
      const e = this.charge.energy || {};
      if (e.retainedBaseKwh == null || e.lossCoefficient == null) return null;
      const m = e.socModel;
      if (e.retainedEstimator === 'SOC' && m && m.mode === 'ev-db-duration') {
        const base = Math.round(e.retainedBaseKwh * 100) / 100;
        const aux = Math.round((m.auxPower || 0) * (m.durationHours || 0) * 100) / 100;
        return `${m.conversion} × ${base} + ${m.auxPower} kW × ${m.durationHours} h (= +${aux}) · EV DB`;
      }
      const src = (e.retainedEstimator === 'SOC' && m && m.mode === 'ev-db-factor') ? ' (EV DB)' : '';
      return `${this.fmtKwh(e.retainedBaseKwh)} × ${e.lossCoefficient}${src}`;
    },

    allEstimators() {
      const e = this.charge.energy || {};
      return [
        { key: 'ENERGY_ADDED', label: this.$t('meter-device.connected-vehicle.estimator.energy-added'), value: e.energyAddedKwh },
        { key: 'ENERGY_REMAINING', label: this.$t('meter-device.connected-vehicle.estimator.remaining'), value: e.energyRemainingDeltaKwh },
        { key: 'SOC', label: this.$t('meter-device.connected-vehicle.estimator.soc'), value: e.socDeltaKwh },
        { key: 'POWER_DURATION', label: this.$t('meter-device.connected-vehicle.estimator.power-duration'), value: e.powerDurationKwh },
      ].map(x => ({ ...x, retained: x.key === e.retainedEstimator }));
    },

    chartOptions() {
      const m = this.current;
      return {
        chart: { height: 380, zoomType: 'x' },
        time: { useUTC: false }, // local time, consistent with the tables
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        // Shade the periods where the vehicle was OFFLINE (unreachable) — it charged out of reach and we only
        // caught the value on reconnection, which is why the curve can jump at the band's edge.
        xAxis: {
          type: 'datetime',
          plotBands: (this.offlinePeriods || []).map(p => ({
            from: new Date(p.from).getTime(),
            to: new Date(p.to).getTime(),
            color: 'rgba(120,120,120,0.12)',
            label: { text: this.$t('meter-device.connected-vehicle.offline'), style: { color: '#9e9e9e', fontSize: '10px' } },
          })),
        },
        yAxis: {
          title: { text: m.unit },
          min: m.key === 'soc' ? 0 : null,
          max: m.key === 'soc' ? 100 : null,
        },
        tooltip: { xDateFormat: '%d/%m %H:%M', valueSuffix: ' ' + m.unit },
        series: [{ name: m.label, data: m.data, color: m.color, marker: { enabled: m.data.length < 60 } }],
      };
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      // Charge-scoped endpoint: the backend resolves the charge's OWN provider + window from the id and returns
      // only that provider's readings — no more date-range fetch that mixed every provider into a sawtooth.
      MeterDeviceRepository.connectedVehicleChargeCurve(this.charge.id).then(res => {
        this.points = res.items || [];
        this.offlinePeriods = res.offlinePeriods || [];
        this.loading = false;
      }).catch(err => {
        this.loading = false;
        this.notifyError(err);
      });
    },

    seriesFor(field, transform) {
      const out = [];
      for (const p of this.points) {
        if (p[field] != null) {
          out.push([new Date(p.readingAt).getTime(), transform ? transform(p[field]) : p[field]]);
        }
      }
      return out;
    },

    fmtKwh(v) {
      return (v === null || v === undefined) ? '—' : (Math.round(v * 1000) / 1000) + ' kWh';
    },

    fmtPct(v) {
      return (v === null || v === undefined) ? '—' : (Math.round(v * 10) / 10) + ' %';
    },
  },
}
</script>
